import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_ITEM_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { empty, ignoreResizeObserverLoopLimit } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { isNumber } from "@cervval.js/common/src/tools/number.tools";
import { toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { timestampToPxY } from "@cervval.js/common/src/tools/timestamps.tools.ui";
import { } from "../dayAgenda/dayAgenda.types";
import { ColumnContext, DayAgendaElementContext, DayAgendaItemContext } from "../dayAgenda/dayAgendaContext";
import { DayAgendaRefreshReason } from "../dayAgenda/dayAgendaEvents";
import { DATA_COLUMN_UID_ATTRIBUTE } from "../dayAgenda/dayAgendaUi.helper.consts";
import { createItemDivContainer, fillItemDiv } from "../dayAgenda/dayAgendaUi.helper.items";

/** AgendaColumnView's context containing its date interval, X position and size. */
export type DayAgendaColumnViewContext = DayAgendaElementContext;

export type DayAgendaColumnViewData<ItemObject, ColumnObject> = {

    /** Context of the AgendaColumnView (date interval, x position and size) */
    context: DayAgendaColumnViewContext,

    /** Information of the  to be displayed by the AgendaColumnView */
    columnContext: ColumnContext<ItemObject, ColumnObject>

}

export interface DayAgendaColumnViewOptions {

}

export const defaultDayAgendaColumnViewOptions: DayAgendaColumnViewOptions = {


};

@CustomElement({
    name: CjsDayAgendaColumnView.TAG_NAME,
    style: require("./dayAgendaColumnView.scss"),
    elementCssClasses: [FLEX_ITEM_AUTO]
})
export class CjsDayAgendaColumnView<ItemObject, ColumnObject> extends WebComponent {

    /** Tag name of DayAgendaColumnView */
    public static readonly TAG_NAME = "cjs-day-agenda-column-view";

    /** 
     * Attributes options of AgendaColumnView
     */
    @TypedWebComponentOptions({ defaultValues: defaultDayAgendaColumnViewOptions, doNotBindOptionsToHtmlAttributes: true })
    public readonly options!: WebComponentOptions<DayAgendaColumnViewOptions>;

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /** Context of the agenda (date interval, x position and size) */
    private _context?: DayAgendaColumnViewContext;

    /** Information of the column to be displayed by the AgendaColumnView */
    public columnContext: ColumnContext<ItemObject, ColumnObject> | undefined;

    /** _resizeObserver will be disconnected on element disconnect */
    protected _resizeObserver?: ResizeObserver;

    /** Lazy rendering observer and data */
    private _lazyRenderInfos?: {
        UID_ATTRIBUTE: string,
        nextAsyncRenderId: number,
        intersectionObserver: IntersectionObserver,
        contextByUid: Record<string, DayAgendaItemContext<ItemObject>>
    };

    constructor(
        data?: DayAgendaColumnViewData<ItemObject, ColumnObject>,
        options?: Partial<DayAgendaColumnViewOptions>,
        customElementOptions?: OverrideCustomElementConfig
    ) {
        super(customElementOptions);

        // Set data if given
        if (data) {
            this._context = data.context;
            this.columnContext = data.columnContext;
        }

        this.options.setWithoutRefresh(options);
    }

    /**
     * The initialization of the AgendaColumnView
     */
    public setData(data: DayAgendaColumnViewData<ItemObject, ColumnObject>): void {
        this._context = data.context;
        this.columnContext = data.columnContext;
        this.refresh(DayAgendaRefreshReason.SET_DATA);
    }

    /**
     * Update current context of columnView (bounds and start/finish)
     */
    protected _updateContext(): void {
        if (!this.columnContext || !this._context) { return; }
        // recompute context if none provided
        const bounds = this.getBoundingClientRect();

        const height = bounds.height;
        this.columnContext.height = height;
        this.columnContext.endY = height + this.columnContext.beginY;

        const width = bounds.width;
        this.columnContext.width = width;
        this.columnContext.beginX = Number(this._context.beginX + this.offsetLeft);
        this.columnContext.endX = width + this.columnContext.beginX;

        this.columnContext = {
            ... this.columnContext,
            ...this._context
        };

        const dateTs = this.columnContext.dayTs;
        const date = toDate(dateTs);
        this.columnContext.startTs = date.beginOf("day").uts;
        this.columnContext.finishTs = date.beginOfNext("day").uts;
    }

    /**
     * This is needed if you use the component standalone (outside the agenda)
     */
    protected _installResizeObserver(): void {
        /** Install resize observer to refresh the agendaTimescale when triggered */
        const resizeObserver = new ResizeObserver(entries => {
            ignoreResizeObserverLoopLimit(entries, () => {
                this.refresh(DayAgendaRefreshReason.RESIZE);
            });
        });
        resizeObserver.observe(this);
        this._resizeObserver = resizeObserver;
    }

    /** When element is removed from DOM */
    public disconnectedCallback(): void {
        // * if element is connected again to the DOM later, we won't add again the resizeObserver (this is ok for now, we don't want to make it complex)
        if (this._resizeObserver != null) {
            this._resizeObserver.disconnect();
            this._resizeObserver = undefined;
        }
    }

    /**
     * On component init
     */
    public override init(): void {
        if (this.columnContext) {
            this.setAttribute(DATA_COLUMN_UID_ATTRIBUTE, this.columnContext.uid);
        }
    }

    /**
     * Callback used in lazy rendering triggered by intersection observer
     * @param entries 
     * @param observer
     */
    protected _asyncRender(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
        if (this._lazyRenderInfos) {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    observer.unobserve(entry.target);
                    const renderUid = entry.target.getAttribute(this._lazyRenderInfos.UID_ATTRIBUTE);
                    if (renderUid) {
                        const itemContext = this._lazyRenderInfos.contextByUid[renderUid];

                        if (!itemContext) {
                            // a new render was triggered
                            continue;
                        }

                        // prevent rerender (should not be needed since we "unobserve"... but just in case)
                        delete this._lazyRenderInfos.contextByUid[renderUid];

                        // if ViewData.innerDiv is set it means append and render were already done
                        if (itemContext.columnViewData && !itemContext.columnViewData.innerDiv) {
                            this._renderItem(itemContext);
                        }
                    }
                }
            }
        }
    }

    /**
     * Append inner div if needed and render item
     */
    protected _renderItem(itemContext: DayAgendaItemContext<ItemObject>): void {
        if (itemContext.columnViewData && !itemContext.columnViewData.innerDiv) {
            // apply style and append inner elements
            itemContext.columnViewData = fillItemDiv(
                itemContext.columnViewData.div,
                itemContext,
                {
                    tooltipContainer: this,
                    isEditable: this._context?.isEditable,
                    isResizable: itemContext.isResizable
                }
            );
        }

        // render
        if (itemContext?.itemRender && itemContext.columnViewData?.innerDiv) {
            itemContext.itemRender(itemContext, itemContext.columnViewData.div, itemContext.columnViewData.innerDiv);
        }
    }

    /**
     * Refresh the agenda  view with its items
     * @param reason refresh reason
     * @param context AgendaColumnView context
     */
    public override refresh(reason?: string, context?: DayAgendaColumnViewContext): void {
        const debugTrace = this.debug.traceStart("refresh", reason);
        try {
            if (context) {
                this._context = { ...context };// Use provided context
            }
            this._updateContext();
            // append items
            this._renderItems();

            this.debug.traceFinish(debugTrace);
        } catch (err) {
            this.debug.traceError(debugTrace, err);
            this._log?.error(err);
        }
    }

    /**
     * Create and draw DOM elements corresponding to the displayed items of the .
     */
    protected _renderItems(reason?: string): void {
        empty(this);

        // clear async context
        if (this._lazyRenderInfos) {
            this._lazyRenderInfos.contextByUid = {};
            this._lazyRenderInfos.intersectionObserver.disconnect();
        }

        if (this.columnContext?.column && this._context) {

            const itemsToAppend: HTMLElement[] = [];

            for (const itemContext of this.columnContext.itemContexts) {

                if (!itemContext.columnViewData) {
                    const div = createItemDivContainer(itemContext);
                    itemContext.columnViewData = { div };
                }

                // compute top and height
                const itemBeginY = timestampToPxY(this.columnContext, { ts: itemContext.startTs });
                const itemEndY = timestampToPxY(this.columnContext, { ts: itemContext.finishTs });

                // Check if begin/end are valid px values
                if (!isNumber(itemBeginY) || !isNumber(itemEndY)) { continue; }

                const top = itemBeginY;
                const height = itemEndY - itemBeginY;

                if (!itemContext.columnViewData) { return; }
                itemContext.columnViewData.div.style.top = top + "px";
                itemContext.columnViewData.div.style.height = height + "px";

                if (itemContext.subColIndex !== 0) {
                    const percent = (100 / itemContext.concurrentItemsCount) * itemContext.subColIndex;
                    itemContext.columnViewData.div.style.left = `calc(100% - ${percent}%)`;
                    itemContext.columnViewData.div.style.width = `calc(100% / ${itemContext.concurrentItemsCount})`;

                }

                if (itemContext.itemRender) {
                    if (this._lazyRenderInfos) {
                        // register context for async render
                        const asyncRenderUid = "" + this._lazyRenderInfos.nextAsyncRenderId++;
                        itemContext.columnViewData.div.setAttribute(this._lazyRenderInfos.UID_ATTRIBUTE, asyncRenderUid);
                        this._lazyRenderInfos.contextByUid[asyncRenderUid] = itemContext;

                        // observe and enable async render
                        this._lazyRenderInfos.intersectionObserver.observe(itemContext.columnViewData.div);
                    } else {
                        // direct render
                        this._renderItem(itemContext);
                    }
                }

                itemsToAppend.push(itemContext.columnViewData.div);

            }
            // only append items once in column
            this.append(...itemsToAppend);
        }
    }

}